import React from 'react';

import { Accordion, Button, HeroLayout, Link, Text } from '@change/ds-core-ui';
import { FlagIcon, SparkleIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { Translate, useI18n } from '@change-corgi/core/react/i18n';
import { isSsr } from '@change-corgi/core/ssr';
import { getWindow } from '@change-corgi/core/window';

import { ReportAbuseLink } from 'src/app/shared/components/abuse';
import { capitalizeTopic } from 'src/app/shared/utils/topic';

import { ImageWithAttribution } from './components/ImageWithAttribution';
import { TopicStats } from './components/TopicStats';
import { usePageHeading } from './hooks/usePageHeading';

export const PageHeading: React.FC = () => {
	const { translate } = useI18n();
	const {
		data: { description, name, hasTopicPhoto, trackSAP, onAccordionToggle, isExpanded },
	} = usePageHeading();

	const capitalizedName = capitalizeTopic(name);

	return (
		<div className={cn('md:mt-16', { 'mt-4': !hasTopicPhoto })}>
			<HeroLayout
				media={hasTopicPhoto && <ImageWithAttribution imgAlt={name} />}
				reverseMd
				variant="1/3"
				fullWidthMediaSm
				className="bg-background-raised"
			>
				<div className="relative flex-shrink-0 flex-grow-0 basis-1/2 rounded-b-xl bg-background-raised md:h-full md:rounded-br-none md:rounded-tl-xl">
					<div className="flex h-full flex-col items-start justify-between gap-6 p-6 md:p-12">
						<div>
							<Text variant="body" className="hidden text-typography-weak md:block">
								<Translate value="corgi.topic_page.page_heading.topic" />
							</Text>

							<Text
								as="h1"
								variant="heading"
								size="x-large"
								className="mb-6 mt-2 text-typography-strong"
								data-qa="topic-heading"
							>
								{capitalizedName}
							</Text>

							<TopicStats />
						</div>

						<Button
							as="a"
							href="/start-a-petition?source_location=topics_page"
							variant="primary"
							size="small"
							onClick={trackSAP}
							className="max-md:w-full"
							data-qa="header-sap-button"
						>
							<Translate value="corgi.topic_page.page_heading.start_button" />
						</Button>
					</div>
				</div>
			</HeroLayout>

			{description && (
				<Accordion
					as="h2"
					data-qa="topic-accordion"
					title={translate('corgi.topic_page.page_heading.accordion_title', { name: capitalizedName })}
					onToggle={(_event, expanded) => {
						onAccordionToggle(expanded);
					}}
				>
					<div className="flex flex-col items-start gap-4 text-typography-strong">
						<div className="bg-surface-secondary-hovered mb-2 flex items-center gap-2 self-start rounded-lg px-2 py-1 text-typography-weak">
							<SparkleIcon size="small" />
							<Translate value="corgi.components.ai_content.label" />
						</div>
						<Text as="p">{description}</Text>

						<ReportAbuseLink pageUrl={isSsr() ? undefined : getWindow().location.href} type="generic">
							<Link
								leadingIcon={<FlagIcon />}
								size="small"
								variant="primary"
								as="button"
								tabIndex={isExpanded ? 0 : -1}
							>
								<Translate value="fe.pages.decision_maker_show.ai_content.report_issue_label" />
							</Link>
						</ReportAbuseLink>
					</div>
				</Accordion>
			)}
		</div>
	);
};
